import { z } from "zod";

/**
 * «مار» — classic Snake, a single-player game played entirely on the phone.
 *
 * The server keeps ONE thing for it: each player's best score at each speed, so it survives a new
 * phone. No match history, no stats, no leaderboard — and Snake is deliberately NOT in `GAMES`, which
 * is the list of games that feed those.
 *
 * The rules' numbers live here because the api's score bounds are derived from them: change a rule
 * and `SNAKE_MAX_SCORES` follows.
 */

/** Cells per side of the square board (the operator, 2026-11-02: twice the first board's 17). */
export const SNAKE_GRID = 34;

/** Cells the snake starts with — the first board's 3, at the finer grid's scale. */
export const SNAKE_INITIAL_LENGTH = 6;

/** Cells one NORMAL food grows the snake by: the first board's 1, at the finer grid's scale. The tail
 *  holds still for that many steps, starting with the step that eats. */
export const SNAKE_GROWTH = 2;

/** Cells one BONUS grows the snake by — twice a normal food (the operator, 2026-11-02). The tail
 *  holds still for that many steps, starting with the step that eats it. */
export const SNAKE_BONUS_GROWTH = 4;

/** The three speeds, slowest first. */
export const SNAKE_SPEED_ORDER = ["slow", "normal", "fast"] as const;
export type SnakeSpeed = (typeof SNAKE_SPEED_ORDER)[number];
export const snakeSpeedSchema = z.enum(SNAKE_SPEED_ORDER);

export interface SnakeSpeedRule {
  /** Milliseconds between two steps of the snake. */
  readonly stepMs: number;
  /** Points one normal food is worth at this speed. */
  readonly points: number;
}

/** About 30% slower than the first 120 / 85 / 60 ms, which were all too fast (the operator,
 *  2026-11-02): a crossing of the 34-cell board takes ≈ 5.8 / 4.3 / 3.1 s. */
export const SNAKE_SPEEDS: Readonly<Record<SnakeSpeed, SnakeSpeedRule>> = {
  slow: { stepMs: 170, points: 1 },
  normal: { stepMs: 125, points: 2 },
  fast: { stepMs: 90, points: 3 },
};

/** A bonus food appears after every `SNAKE_BONUS_EVERY`-th normal food. */
export const SNAKE_BONUS_EVERY = 5;

/** A bonus lasts this many steps — the first board's 20 steps, the same distance on the finer one. */
export const SNAKE_BONUS_STEPS = 40;

/**
 * A bonus's value drops by one food's points every `SNAKE_BONUS_TICK` steps:
 * `points × ⌈stepsLeft / SNAKE_BONUS_TICK⌉` — 20 × points when it appears, down to 1 × points.
 */
export const SNAKE_BONUS_TICK = 2;

/** A bonus is a `SNAKE_BONUS_SIZE × SNAKE_BONUS_SIZE` block of cells. */
export const SNAKE_BONUS_SIZE = 2;

/**
 * THE SCORE BOUNDS — a TRUE CEILING at each speed, which the api never lets a score pass: the exact
 * maximum under N + B ≤ 1150, B ≤ ⌊N / 5⌋ and a bonus ≤ 20 × points. It is not what real play
 * reaches: with the growth of 2 a food and 4 a bonus, a game peaks near 2052 × points.
 *
 * Every eat step holds the tail, so it lengthens the snake by one cell on that very step — whatever
 * the growth (≥ 1, the engine's guard), the rest comes on later steps — so one game holds at most
 * 34² − 6 = 1150 eats, normal foods N and bonuses B together: N + B ≤ 1150. A bonus comes only after
 * every 5th normal food (B ≤ ⌊N / 5⌋, so 6B ≤ 1150: B ≤ 191) and is worth at most ⌈40 / 2⌉ = 20
 * foods. So every game has N + 20B ≤ (1150 − B) + 20B = 1150 + 19B ≤ 1150 + 19 × 191 = 4779 foods'
 * worth — and N = 959, B = 191 meets every one of those counts (⌊959 / 5⌋ = 191), so no smaller
 * number bounds them: 4779 × points.
 */
const maxEats = SNAKE_GRID * SNAKE_GRID - SNAKE_INITIAL_LENGTH;
const maxBonuses = Math.floor(maxEats / (SNAKE_BONUS_EVERY + 1));
const maxFoods = maxEats - maxBonuses;
/** A bonus is worth the most on the step after it appears. */
const maxBonusFoods = Math.ceil(SNAKE_BONUS_STEPS / SNAKE_BONUS_TICK);
/** The bound in foods of a game's own speed — 4779. */
const maxFoodsWorth = maxFoods + maxBonuses * maxBonusFoods;

/** The highest score the api accepts at each speed: slow 4779, normal 9558, fast 14337. */
export const SNAKE_MAX_SCORES: Readonly<Record<SnakeSpeed, number>> = {
  slow: maxFoodsWorth * SNAKE_SPEEDS.slow.points,
  normal: maxFoodsWorth * SNAKE_SPEEDS.normal.points,
  fast: maxFoodsWorth * SNAKE_SPEEDS.fast.points,
};

/** A score: a whole number, never negative. */
const snakeScore = z.number().int().min(0);

/**
 * `POST /me/snake/score` — a finished game's score AT ITS SPEED, 0..that speed's bound. The server
 * keeps, at that speed, max(stored best, score).
 */
export const submitSnakeScoreSchema = z
  .object({ speed: snakeSpeedSchema, score: snakeScore })
  .refine((input) => input.score <= SNAKE_MAX_SCORES[input.speed], {
    message: "Above the highest score this speed allows",
    path: ["score"],
  });
export type SubmitSnakeScoreInput = z.infer<typeof submitSnakeScoreSchema>;

/** The player's best at each speed; 0 at a speed they have never finished a game at. */
export const snakeBestsSchema = z.object({
  slow: snakeScore,
  normal: snakeScore,
  fast: snakeScore,
}) satisfies z.ZodType<Record<SnakeSpeed, number>>;
export type SnakeBests = z.infer<typeof snakeBestsSchema>;

/** `GET /me/snake/best` and `POST /me/snake/score` — the player's best at each speed. */
export const snakeBestResponseSchema = z.object({
  best: snakeBestsSchema,
});
export type SnakeBestResponse = z.infer<typeof snakeBestResponseSchema>;
